import type { RefObject } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import type { IJourneyDetails } from '../../../../../../models/journey-details.model';
import type { IJourney } from '../../../../../../models/journey.model';
import JourneySection from './components/JourneySection';
import MicromobilityLinkSection from './components/MicromobilityLinkSection';
import AccessLink from './components/AccessLink';
import { LegHelper } from '../../../../../../helpers/leg.helper';
import type { ITripLeg } from '../../../../../../models/trip-leg.model';
import type { IConnectionLink } from '../../../../../../models/connection-link.model';
import TripLeg from './components/TripLeg';
import ConnectionLink from './components/ConnectionLink';
import Toolbar from './components/Toolbar';
import type { ITripLegDetails } from '../../../../../../models/trip-leg-details.model';
import TripLegCalls from './components/TripLegCalls';
import type { IServiceJourney } from '../../../../../../models/service-journey.model';
import { LineHelper } from '../../../../../../helpers/line.helper';
import DestinationLink from './components/DestinationLink';
import Notes from './components/TripLeg/components/Notes';
import PrintSummary from './components/PrintSummary';
import type { IConnectionLinkDetails } from '../../../../../../models/connection-link-details.model';
import { AnalyticsService } from '../../../../../../services/analytics-service';
import { LineType } from './components/LineMarker/line-markers.enum';
import MicromobilityLeg from './components/MicromobilityLinkSection/components/MicromobilityLeg';
import MicromobilityHelper from '../../../../helpers/micromobility.helper';
import { IMicromobilityLink } from '../../../../../../models/labs/micromobility/micromobility-link.model';
import MicromobilityCalls from './components/MicromobilityLinkSection/components/MicromobilityCalls';
import LogService from '../../../../../../services/log.service';
import { FeatureFlagService } from '../../../../../../services/feature-flag.service';

function getDetailsForTripLeg(tripLeg: ITripLeg, journeyDetails: IJourneyDetails | undefined): ITripLegDetails | undefined {
  const tripLegDetails = journeyDetails?.tripLegs?.find((leg) => leg.journeyLegIndex === tripLeg.journeyLegIndex);
  if (tripLegDetails) return tripLegDetails;
  LogService.logError(new Error('Trip leg details not found'), 'No trip leg found in details with the matching index');
  return undefined;
}

function getDetailsForConnectionLink(connectionLink: IConnectionLink, journeyDetails: IJourneyDetails | undefined): IConnectionLinkDetails | undefined {
  return journeyDetails?.connectionLinks?.find((cl) => cl.journeyLegIndex === connectionLink.journeyLegIndex);
}

function isElementCompletelyVisible(element?: HTMLElement): boolean {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
}

function Details({
  journey,
  journeyDetails,
  journeyMapRef,
  onServiceJourneySelected,
  onMicromobilityLinkSelected,
  onUpdateAvailableVehicles,
}: {
  journey: IJourney;
  journeyDetails: IJourneyDetails;
  journeyMapRef: RefObject<HTMLDivElement | null>;
  onServiceJourneySelected: (serviceJourney: IServiceJourney | undefined) => void;
  onMicromobilityLinkSelected: (micromobilityLink: IMicromobilityLink | undefined) => void;
  onUpdateAvailableVehicles: () => void;
}) {
  const [selectedTripLeg, setSelectedTripLeg] = useState<ITripLeg | undefined>(undefined);
  const [selectedMicromobilityLink, setSelectedMicromobilityLink] = useState<IMicromobilityLink | undefined>(undefined);

  const printContentRef = useRef<HTMLDivElement>(null);
  const tripLegCallsRef = useRef<HTMLDivElement>(null);
  const micromobilityCallsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedTripLeg && (!isElementCompletelyVisible(journeyMapRef.current!) || !isElementCompletelyVisible(tripLegCallsRef.current!))) {
      journeyMapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (selectedMicromobilityLink && (!isElementCompletelyVisible(journeyMapRef.current!) || !isElementCompletelyVisible(micromobilityCallsRef.current!))) {
      journeyMapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [journeyMapRef, selectedTripLeg, selectedMicromobilityLink]);

  return (
    <div ref={printContentRef}>
      <PrintSummary journey={journey} />
      {!selectedTripLeg && !selectedMicromobilityLink && (
        <ol className="journey-details-details__list list-unstyled mb-4 overflow-hidden">
          {journey.departureAccessLink && (
            <JourneySection originEndpoint={journey.departureAccessLink.origin} lineType={LineType.dotted}>
              <AccessLink departureAccessLink={journey.departureAccessLink} />
            </JourneySection>
          )}
          {journey.destinationLink && (
            <JourneySection originEndpoint={journey.destinationLink.origin} destinationEndpoint={journey.destinationLink.destination} lineType={LineType.dotted}>
              <DestinationLink destinationLink={journey.destinationLink} />
            </JourneySection>
          )}

          {/* MICRIMOBILITY START LINK BEGIN*/}
          {journey.startingMicromobilityLink && (
            <Fragment key="start-micromobility-link">
              <MicromobilityLinkSection
                micromobilityLink={journey.startingMicromobilityLink}
                className="mb-2"
                endDestination={MicromobilityHelper.getEndDestinationNameForMicromobilityDocklessLink(journey.startingMicromobilityLink, true, journey.tripLegs?.[0])}
              >
                <MicromobilityLeg
                  micromobilityLink={journey.startingMicromobilityLink}
                  onMicromobilityLinkSelected={(micromobilityLink) => {
                    setSelectedMicromobilityLink(micromobilityLink);
                    onMicromobilityLinkSelected(micromobilityLink);
                    AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Visa detaljvy delresa mikromobilitet', '');
                  }}
                  onUpdateAvailableVehicles={onUpdateAvailableVehicles}
                />
              </MicromobilityLinkSection>
            </Fragment>
          )}
          {/* MICRIMOBILITY START LINK END*/}

          {journey.allLegs?.map((leg, index) => {
            if (LegHelper.isTripLeg(leg)) {
              const tripLeg = leg as ITripLeg;
              const nextTripLeg = journey.allLegs && index < journey.allLegs.length && LegHelper.isTripLeg(journey.allLegs[index + 1]) ? (journey.allLegs[index + 1] as ITripLeg) : undefined;
              const hasSamePlatform =
                nextTripLeg && nextTripLeg.origin.stopPoint.gid === tripLeg.destination.stopPoint.gid && nextTripLeg.origin.stopPoint.platform === tripLeg.destination.stopPoint.platform;
              const tripLegDetails = getDetailsForTripLeg(tripLeg, journeyDetails);
              const guaranteedChangeNotes = LegHelper.getGuaranteedChangeNotes(tripLeg);

              return (
                <Fragment key={`trip-leg-${leg.journeyLegIndex}`}>
                  <JourneySection
                    origin={tripLeg.origin}
                    destination={tripLeg.destination}
                    line={tripLeg.serviceJourney.line}
                    lineColor={LineHelper.getBackgroundSafeForegroundColor(tripLeg.serviceJourney.line)}
                    isOriginCancelled={tripLeg.isCancelled || (tripLegDetails?.callsOnTripLeg && tripLegDetails.callsOnTripLeg[0].isCancelled)}
                    isDestinationCancelled={tripLeg.isCancelled || (tripLegDetails?.callsOnTripLeg && tripLegDetails.callsOnTripLeg[tripLegDetails.callsOnTripLeg.length - 1].isCancelled)}
                    className={hasSamePlatform || journey.endingMicromobilityLink ? 'mb-2' : ''}
                  >
                    {tripLeg && tripLegDetails && (
                      <TripLeg
                        tripLeg={tripLeg}
                        tripLegDetails={tripLegDetails}
                        onTriplegSelected={(tripleg) => {
                          setSelectedTripLeg(tripleg);
                          onServiceJourneySelected(tripLeg.serviceJourney);
                          AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Visa detaljvy delresa', '');
                        }}
                      />
                    )}
                  </JourneySection>
                  {guaranteedChangeNotes.length > 0 && (
                    <JourneySection lineType={LineType.none} className="mb-2">
                      <Notes notes={guaranteedChangeNotes} />
                    </JourneySection>
                  )}
                </Fragment>
              );
            }
            if (LegHelper.isConnectionLink(leg)) {
              const connectionLink = leg as IConnectionLink;
              const connectionLinkDetails = getDetailsForConnectionLink(connectionLink, journeyDetails);
              const guaranteedChangeNotes = LegHelper.getGuaranteedChangeNotes(leg);
              return (
                <JourneySection key={`connection-link-${leg.journeyLegIndex}`} lineType={LineType.dotted}>
                  {guaranteedChangeNotes.length > 0 && <Notes notes={guaranteedChangeNotes} className="mb-2" />}
                  {connectionLinkDetails && <ConnectionLink connectionLinkDetails={connectionLinkDetails} />}
                </JourneySection>
              );
            }
            return null;
          })}

          {/* MICRIMOBILITY ENDING LINK BEGIN*/}
          {journey.endingMicromobilityLink && (
            <Fragment key="ending-micromobility-link">
              <MicromobilityLinkSection
                className="mb-2"
                micromobilityLink={journey.endingMicromobilityLink}
                endDestination={MicromobilityHelper.getEndDestinationNameForMicromobilityDocklessLink(journey.endingMicromobilityLink, false, journey.tripLegs?.[0])}
              >
                <MicromobilityLeg
                  micromobilityLink={journey.endingMicromobilityLink}
                  onMicromobilityLinkSelected={(micromobilityLink) => {
                    setSelectedMicromobilityLink(micromobilityLink);
                    onMicromobilityLinkSelected(micromobilityLink);
                    AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Visa detaljvy delresa mikromobilitet', '');
                  }}
                  onUpdateAvailableVehicles={onUpdateAvailableVehicles}
                />
              </MicromobilityLinkSection>
            </Fragment>
          )}
          {/* MICRIMOBILITY ENDING LINK END*/}

          {journey.arrivalAccessLink && (
            <JourneySection destinationEndpoint={journey.arrivalAccessLink.destination} lineType={LineType.dotted}>
              <AccessLink arrivalAccessLink={journey.arrivalAccessLink} />
            </JourneySection>
          )}
        </ol>
      )}
      {selectedTripLeg && (
        <div className="mb-4" ref={tripLegCallsRef}>
          <TripLegCalls
            tripLeg={selectedTripLeg}
            tripLegDetails={getDetailsForTripLeg(selectedTripLeg, journeyDetails)}
            journeyId={journey.id}
            onClose={() => {
              setSelectedTripLeg(undefined);
              onServiceJourneySelected(undefined);
            }}
          />
        </div>
      )}
      {selectedMicromobilityLink && (
        <div className="mb-4" ref={micromobilityCallsRef}>
          <MicromobilityCalls
            micromobilityLink={selectedMicromobilityLink}
            journeyId={journey.id}
            firstTripLegOnJourney={journey.tripLegs?.[0]}
            onClose={() => {
              setSelectedMicromobilityLink(undefined);
              onMicromobilityLinkSelected(undefined);
            }}
            onUpdateAvailableVehicles={onUpdateAvailableVehicles}
          />
        </div>
      )}
      {!FeatureFlagService.enableMicromobilitySearch && <Toolbar journey={journey} printContentRef={printContentRef} />}
    </div>
  );
}

export default Details;
